import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { canAccessPlatform } from "../utils/permissions";
import {
  Users,
  VideoCamera,
  Brain,
  ChatCircle,
  Buildings,
  Certificate,
  Sparkle,
  ShieldCheck,
  Lightning,
  TrendUp,
  ArrowClockwise,
  SpinnerGap,
  Crown,
  CurrencyDollar,
  Exam,
  Warning,
  CheckCircle,
  XCircle,
  Clock,
  ChartBar
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

export default function PlatformDashboardPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Onboarding scheduler
  const [schedulerRunning, setSchedulerRunning] = useState(false);
  const [schedulerResult, setSchedulerResult] = useState(null);
  const [copiedUptime, setCopiedUptime] = useState(false);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/platform/dashboard`, { withCredentials: true });
      setData(d);
    } catch (err) {
      console.error("Failed to load platform dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, []);

  const runOnboardingScheduler = async (dryRun) => {
    setSchedulerRunning(true);
    setSchedulerResult(null);
    try {
      const { data: d } = await axios.post(
        `${API}/api/admin/onboarding/send-scheduled-emails?dry_run=${dryRun ? "true" : "false"}`,
        {},
        { withCredentials: true },
      );
      setSchedulerResult(d);
    } catch (e) {
      setSchedulerResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setSchedulerRunning(false);
    }
  };

  if (!canAccessPlatform(user)) {
    return <div className="p-8 text-center text-muted-foreground">Acces reserve aux fondateurs KNORIA</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-cyan-400" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-8 text-center text-muted-foreground">Erreur de chargement</div>;
  }

  const m = data.metrics;
  const maxActivity = Math.max(...data.activity_timeline.map(d => d.logins + d.queries), 1);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="platform-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center">
            <Crown size={24} weight="fill" className="text-cyan-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit'] tracking-tight" data-testid="platform-dashboard-title">
              {t("platform_dashboard")}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t("platform_realtime")} — {new Date(data.generated_at).toLocaleString("fr-FR")}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadDashboard} className="gap-2" data-testid="refresh-dashboard-btn">
          <ArrowClockwise size={14} />
          Rafraichir
        </Button>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="metrics-grid">
        <MetricCard
          icon={Users} label="Utilisateurs" value={m.users.total}
          sub={`+${m.users.new_week} {t("this_week")}`}
          color="cyan" testId="metric-users"
        />
        <MetricCard
          icon={VideoCamera} label="Videos" value={m.videos.total}
          sub={`${m.videos.complete} terminées`}
          color="blue" testId="metric-videos"
        />
        <MetricCard
          icon={Buildings} label="Organisations" value={m.organizations.total}
          sub={t("active_companies")}
          color="violet" testId="metric-orgs"
        />
        <MetricCard
          icon={Brain} label="Connaissances" value={m.knowledge.total}
          sub={t("knowledge_extracted")}
          color="emerald" testId="metric-knowledge"
        />
        <MetricCard
          icon={ChatCircle} label="Questions IA" value={m.queries.total}
          sub={t("rag_queries")}
          color="amber" testId="metric-queries"
        />
      </div>

      {/* Second Row Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniMetric icon={Exam} label="Quiz completes" value={m.quizzes.total} testId="metric-quizzes" />
        <MiniMetric icon={Certificate} label="Certifications" value={m.certificates.total} testId="metric-certs" />
        <MiniMetric icon={Sparkle} label="Videos IA" value={m.ai_videos.total} testId="metric-ai-videos" />
        <MiniMetric icon={Lightning} label="Feature Flags" value={`${m.feature_flags.active}/${m.feature_flags.total}`} testId="metric-flags" />
      </div>

      {/* Activity Timeline + Video Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Activity Chart */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5" data-testid="activity-chart">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <ChartBar size={18} className="text-cyan-400" />
              <h2 className="font-semibold font-['Outfit'] text-sm">Activité (7 derniers jours)</h2>
            </div>
            <div className="flex items-center gap-4 text-[11px]">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-cyan-500" /> Connexions</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> Questions IA</span>
            </div>
          </div>
          <div className="flex items-end gap-2 h-40">
            {data.activity_timeline.map((day, i) => {
              const loginH = maxActivity > 0 ? (day.logins / maxActivity) * 100 : 0;
              const queryH = maxActivity > 0 ? (day.queries / maxActivity) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`activity-bar-${i}`}>
                  <div className="w-full flex items-end gap-0.5 h-32">
                    <div
                      className="flex-1 bg-cyan-500/80 rounded-t-sm transition-all duration-500 min-h-[2px]"
                      style={{ height: `${Math.max(loginH, 2)}%` }}
                      title={`${day.logins} connexions`}
                    />
                    <div
                      className="flex-1 bg-amber-500/80 rounded-t-sm transition-all duration-500 min-h-[2px]"
                      style={{ height: `${Math.max(queryH, 2)}%` }}
                      title={`${day.queries} questions`}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground">{day.day}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Video Pipeline Status */}
        <div className="bg-card border border-border rounded-xl p-5" data-testid="video-pipeline">
          <div className="flex items-center gap-2 mb-4">
            <VideoCamera size={18} className="text-blue-400" />
            <h2 className="font-semibold font-['Outfit'] text-sm">Pipeline Video</h2>
          </div>
          <div className="space-y-3">
            <PipelineBar label="Terminées" value={m.videos.complete} total={m.videos.total} color="bg-teal-500" icon={CheckCircle} />
            <PipelineBar label="En cours" value={m.videos.processing} total={m.videos.total} color="bg-blue-500" icon={Clock} />
            <PipelineBar label="Échouées" value={m.videos.failed} total={m.videos.total} color="bg-rose-500" icon={XCircle} />
          </div>
          <div className="mt-5 pt-4 border-t border-border">
            <div className="text-center">
              <div className="text-3xl font-bold font-['Outfit'] text-foreground">{m.videos.total}</div>
              <p className="text-xs text-muted-foreground mt-1">{t("total_videos_label")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Organizations + Critical Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Organizations */}
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="orgs-section">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Buildings size={18} className="text-violet-400" />
              <h2 className="font-semibold font-['Outfit'] text-sm">{t("organizations")}</h2>
            </div>
            <span className="text-xs text-muted-foreground">{data.organizations.length} entreprises</span>
          </div>
          {data.organizations.length > 0 ? (
            <div className="divide-y divide-border">
              {data.organizations.map((org) => (
                <div key={org.id} className="px-5 py-3.5 flex items-center justify-between hover:bg-secondary/30 transition-colors" data-testid={`org-row-${org.id}`}>
                  <div>
                    <p className="text-sm font-medium">{org.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {org.user_count} utilisateur{org.user_count !== 1 ? "s" : ""} — Plan: {org.plan || "starter"}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    org.status === "active" ? "bg-teal-500/10 text-teal-400 border border-teal-500/20" : "bg-secondary text-muted-foreground border border-border"
                  }`}>
                    {org.status || "actif"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground text-sm">Aucune organisation</div>
          )}
        </div>

        {/* Critical Events */}
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="critical-events">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Warning size={18} className="text-amber-400" />
              <h2 className="font-semibold font-['Outfit'] text-sm">{t("recent_alerts")}</h2>
            </div>
            <Link to="/dashboard/platform/audit-logs" className="text-xs text-primary hover:underline" data-testid="view-all-alerts-link">
              Voir tout
            </Link>
          </div>
          {data.critical_events.length > 0 ? (
            <div className="divide-y divide-border">
              {data.critical_events.map((evt, i) => (
                <div key={i} className="px-5 py-3.5 flex items-start gap-3 hover:bg-secondary/30 transition-colors" data-testid={`critical-event-${i}`}>
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${evt.severity === "critical" ? "bg-rose-500" : "bg-amber-500"}`} />
                  <div className="min-w-0">
                    <code className="text-xs font-mono">{evt.action}</code>
                    <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                      {evt.actor_email || "Système"} — {evt.created_at ? new Date(evt.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground text-sm">
              <ShieldCheck size={28} className="mx-auto mb-2 opacity-30" />
              {t("no_critical_alert")}
            </div>
          )}
        </div>
      </div>

      {/* Platform Ops: Onboarding scheduler + UptimeRobot hooks */}
      <div className="bg-card border border-border rounded-xl p-5" data-testid="platform-ops-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="font-semibold font-['Outfit'] flex items-center gap-2">
              <Lightning size={18} className="text-amber-500" weight="duotone" />
              Opérations plateforme
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Declenche manuellement la sequence d'onboarding J+1 / J+3 ou copie les endpoints a
              brancher sur UptimeRobot / un cron externe.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Scheduler trigger */}
          <div className="border border-border rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">Emails d'onboarding J+1 / J+3</p>
            <p className="text-[11px] text-muted-foreground mb-3">
              Scanne les inscrits des 5 derniers jours, envoie les relances non encore envoyees.
              Idempotent via <code className="text-[10px] bg-muted px-1 py-0.5 rounded">users.onboarding_emails_sent</code>.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => runOnboardingScheduler(true)}
                disabled={schedulerRunning}
                data-testid="onboarding-dry-run-btn"
              >
                {schedulerRunning ? <SpinnerGap size={14} className="animate-spin mr-1.5" /> : <CheckCircle size={14} className="mr-1.5" />}
                Dry-run (aucun envoi)
              </Button>
              <Button
                size="sm"
                className="bg-[#002FA7] hover:bg-[#001F7A] text-white"
                onClick={() => runOnboardingScheduler(false)}
                disabled={schedulerRunning}
                data-testid="onboarding-send-btn"
              >
                {schedulerRunning ? <SpinnerGap size={14} className="animate-spin mr-1.5" /> : <Sparkle size={14} className="mr-1.5" />}
                Envoyer maintenant
              </Button>
            </div>
            {schedulerResult && (
              <div className="mt-3 text-[11px] bg-zinc-50 border border-zinc-200 rounded p-2 font-mono whitespace-pre-wrap" data-testid="onboarding-scheduler-result">
                {JSON.stringify(schedulerResult, null, 2)}
              </div>
            )}
          </div>

          {/* UptimeRobot hooks */}
          <div className="border border-border rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">Monitoring externe</p>
            <p className="text-[11px] text-muted-foreground mb-3">
              Branche ces endpoints sur UptimeRobot (HTTP 5 min) et un cron quotidien (Supabase Scheduled Functions, Vercel cron, GitHub Actions).
            </p>
            <div className="space-y-2 text-[11px]">
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-muted px-2 py-1 rounded truncate" data-testid="uptime-health-url">
                  GET {API}/api/health/public
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${API}/api/health/public`);
                    setCopiedUptime("health");
                    setTimeout(() => setCopiedUptime(false), 1500);
                  }}
                  className="text-[10px] text-[#002FA7] hover:underline"
                  data-testid="copy-uptime-health"
                >
                  {copiedUptime === "health" ? "Copie !" : "Copier"}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground pl-1">
                Public, sans auth. Retourne <code>200 {"{status:'ok'}"}</code> si la DB repond.
              </p>

              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 bg-muted px-2 py-1 rounded truncate" data-testid="uptime-onboarding-url">
                  POST {API}/api/admin/onboarding/send-scheduled-emails
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${API}/api/admin/onboarding/send-scheduled-emails`);
                    setCopiedUptime("onboarding");
                    setTimeout(() => setCopiedUptime(false), 1500);
                  }}
                  className="text-[10px] text-[#002FA7] hover:underline"
                  data-testid="copy-uptime-onboarding"
                >
                  {copiedUptime === "onboarding" ? "Copie !" : "Copier"}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground pl-1">
                Requiert un Bearer token admin. Fréquence recommandee : 1x par jour (UTC).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Events Counter */}
      <div className="bg-gradient-to-r from-[#001030] via-[#001845] to-[#002FA7] rounded-xl p-5 text-white" data-testid="audit-summary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ShieldCheck size={28} className="text-cyan-300" />
            <div>
              <h3 className="font-semibold font-['Outfit']">Sécurité & Conformité</h3>
              <p className="text-blue-200 text-xs mt-0.5">{m.audit_events.total} evenements d'audit enregistres</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold font-['Outfit']">{m.feature_flags.active}</div>
              <p className="text-[10px] text-blue-200">Flags actifs</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold font-['Outfit']">${m.revenue.total.toFixed(0)}</div>
              <p className="text-[10px] text-blue-200">Revenus totaux</p>
            </div>
            <Link
              to="/dashboard/platform/audit-logs"
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-xs font-medium hover:bg-white/20 transition-colors"
              data-testid="go-audit-logs-link"
            >
              Voir les logs
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color, testId }) {
  const colorMap = {
    cyan: "from-cyan-500/15 to-cyan-600/5 border-cyan-500/20 text-cyan-400",
    blue: "from-blue-500/15 to-blue-600/5 border-blue-500/20 text-blue-400",
    violet: "from-violet-500/15 to-violet-600/5 border-violet-500/20 text-violet-400",
    emerald: "from-teal-500/15 to-teal-600/5 border-teal-500/20 text-teal-400",
    amber: "from-amber-500/15 to-amber-600/5 border-amber-500/20 text-amber-400",
  };
  const c = colorMap[color] || colorMap.cyan;
  return (
    <div className={`bg-gradient-to-br ${c} border rounded-xl p-4`} data-testid={testId}>
      <Icon size={20} weight="duotone" className={c.split(" ").pop()} />
      <div className="mt-3">
        <div className="text-2xl font-bold font-['Outfit'] text-foreground">{value}</div>
        <p className="text-xs font-medium text-foreground mt-0.5">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function MiniMetric({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3" data-testid={testId}>
      <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-muted-foreground" />
      </div>
      <div>
        <div className="text-lg font-bold font-['Outfit']">{value}</div>
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function PipelineBar({ label, value, total, color, icon: Icon }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Icon size={13} weight="fill" />
          {label}
        </span>
        <span className="font-semibold font-['Outfit']">{value}</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
